import { GetBrowserUserValues } from 'Clutch/Utilities/CookieUtilties/CookieReader'
import { getSessionDuration } from 'Clutch/Utilities/EngagedSessionStorageUtility/EngagedSessionStorageUtility'
import { getSuperMarketAffinity } from 'Clutch/Utilities/Instrumentation/SegmentUtilities/segmentFunctions'
import { StorageAccessor } from 'Clutch/Utilities/LocalStorageUtility'

import useAcquisitionType from 'Clutch/Hooks/useAcquisition'
import useRecentHistory from 'Clutch/Hooks/useRecentHistory'

// Protocols requires image_url to be non-blank, so return undefined (which sanitize() drops) rather than ''.
// `images` only carries sku-base images; index-option images live in `productImages`.
const getSegmentImageUrl = ({ images, productImages, displayImageUrl } = {}) => {
  const url = images?.[0] || productImages?.[0]?.url || displayImageUrl
  if (!url || !url.trim()) {
    return undefined
  }
  return url.startsWith('//') ? `https:${url}` : url
}

const sendSegmentCheckFitment = (engineIds, skuData, sendSegmentTrackEventWithName) => {
  const segmentEvent = {
    sku: skuData.skuBaseNumber,
    variant: skuData.skuVariantNumber ? skuData.skuVariantNumber : null,
    engineIds: engineIds,
  }
  sendSegmentTrackEventWithName('Unverified Engine Used', segmentEvent)
}

const sendSegmentProductViewed = (data, setMisoId, sendSegmentTrackEventWithName) => {
  try {
    if (!GetBrowserUserValues().isGpcEnabled && data.partNumber != null) {
      //log rocket sessions are tied to this log, don't remove without updating LR first
      console.info('sending Segment Product Viewed event')
      const { list_id, list_name, attribution } = useRecentHistory.GetMostRecentProductListObject()

      let misoId
      try {
        const misoIdData = useRecentHistory.GetAndDeleteMisoId()
        if (misoIdData && misoIdData.skuBase === data.skuBaseNumber) {
          misoId = misoIdData.misoId
          setMisoId(misoId)
        }
      } catch (err) {
        console.error(err)
      }

      let acquisition
      if (!attribution?.acquisition) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useAcquisitionType((acquisitionType) => {
          acquisition = acquisitionType
        })
      }

      let timeToProductView
      try {
        if (!StorageAccessor.sessionStorage.getObj('hasViewedProduct')) {
          StorageAccessor.sessionStorage.setObj('hasViewedProduct', true)
          timeToProductView = getSessionDuration()
        }
      } catch (err) {
        console.error(err)
      }

      const segmentEvent = {
        sku: data.skuBaseNumber,
        variant: data.skuVariantNumber ? data.skuVariantNumber : null, // this will be undefined for sku base pages
        product_id: data.skuBaseNumber,
        name: data.title,
        brand: data.brand?.brandName,
        price: data.retailLowPrice,
        made_in_usa: data.isMadeInUsa,
        new_product: data.isNewProduct,
        list_id: list_id,
        list_name: list_name,
        quantity: 1, // for viewing a product, qty of 1 seems a good default
        url: data.productPageUrl,
        image_url: getSegmentImageUrl(data),
        category: data.segmentCategorization,
        availability: data.availabilityEnum,
        rating: data.reviewCount ? data.rating : 0,
        reviews: data.reviewCount,
        audience_affinity: getSuperMarketAffinity(data),
        value: data.retailLowPrice, // same as price for qty = 1 // do we still want/need this?
        ...(attribution || { acquisition }),
      }

      if (timeToProductView) {
        segmentEvent.time_to_first_event = timeToProductView
      }

      if (misoId) {
        segmentEvent.miso = { miso_id: misoId }
      }

      sendSegmentTrackEventWithName('Product Viewed', segmentEvent)
    }
  } catch (err) {
    console.error('There was an error sending the segment product viewed event', err)
  }
}

const sendSegmentConfiguratorViewed = (data, sendSegmentTrackEventWithName) => {
  try {
    if (!GetBrowserUserValues().isGpcEnabled) {
      //log rocket sessions are tied to this log, don't remove without updating LR first
      console.info('sending Segment Configurator Viewed event')
      const { list_id, list_name, attribution } = useRecentHistory.GetMostRecentProductListObject()

      // Removed MISO stuff

      let acquisition
      if (!attribution?.acquisition) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useAcquisitionType((acquisitionType) => {
          acquisition = acquisitionType
        })
      }

      let timeToProductView
      try {
        if (!StorageAccessor.sessionStorage.getObj('hasViewedProduct')) {
          StorageAccessor.sessionStorage.setObj('hasViewedProduct', true)
          timeToProductView = getSessionDuration()
        }
      } catch (err) {
        console.error(err)
      }

      const segmentEvent = {
        name: data.title,
        brand: data.brand?.brandName,
        price: data.retailLowPrice,
        made_in_usa: data.isMadeInUsa,
        list_id: list_id,
        list_name: list_name,
        quantity: 1, // for viewing a product, qty of 1 seems a good default
        url: data.configuratorPageUrl,
        image_url: getSegmentImageUrl(data),
        category: data.segmentCategorization,
        availability: data.availabilityEnum,
        rating: data.reviewCount ? data.rating : 0,
        reviews: data.reviewCount,
        audience_affinity: getSuperMarketAffinity(data),
        value: data.retailLowPrice, // same as price for qty = 1 // do we still want/need this?
        ...(attribution || { acquisition }),
      }

      if (timeToProductView) {
        segmentEvent.time_to_first_event = timeToProductView
      }

      // Removed MISO stuff

      sendSegmentTrackEventWithName('Configurator Viewed', segmentEvent)
    }
  } catch (err) {
    console.error('There was an error sending the segment configurator viewed event', err)
  }
}

const trackGoogleRemarketing = (data, retailLowPrice, reportingSkuVariantNumber) => {
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      value: data?.retailLowPrice > 0 ? data?.retailLowPrice : retailLowPrice,
      items: [
        {
          id: data?.partNumber ? data.reportingSkuVariantNumber : reportingSkuVariantNumber,
          google_business_vertical: 'retail',
        },
      ],
      send_to: 'ads',
    })
  }
}

const fbTracking = (sendFacebookEvent, skuVariantNumber, title) => {
  if (typeof skuVariantNumber === 'undefined') {
    return
  }

  sendFacebookEvent({
    type: 'track',
    name: 'ViewContent',
    params: {
      content_name: title,
      content_ids: [skuVariantNumber],
      content_type: 'product',
    },
  })
}

export {
  fbTracking,
  getSegmentImageUrl,
  trackGoogleRemarketing,
  sendSegmentProductViewed,
  sendSegmentCheckFitment,
  sendSegmentConfiguratorViewed,
}
